import React from 'react';
import { QuestionRenderer } from './QuestionRenderer';

interface Question {
  id: string;
  type: 'yesno' | 'text' | 'select' | 'date' | 'consent';
  title: string;
  description?: string;
  required?: boolean;
  options?: string[];
  validation?: { 
    pattern?: string; 
    minLength?: number; 
    maxLength?: number; 
  };
}

interface QuestionStepProps {
  question: Question;
  answer: any;
  onAnswerChange: (questionId: string, answer: any) => void;
  onNext: () => void;
  onPrevious: () => void;
  canProceed: boolean;
  isFirst: boolean;
  isLast: boolean;
  stepNumber: number;
  totalSteps: number;
}

export function QuestionStep({
  question,
  answer,
  onAnswerChange,
  onNext,
  onPrevious,
  canProceed,
  isFirst,
  isLast,
  stepNumber,
  totalSteps
}: QuestionStepProps) {
  
  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' && canProceed) {
      onNext();
    }
  };

  return (
    <div className="p-8 md:p-12" onKeyDown={handleKeyDown}>
      {/* Question Header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-8 bg-amber-500 rounded-full flex items-center justify-center text-white font-semibold text-sm">
            {stepNumber}
          </div>
          <span className="text-gray-500 text-sm font-medium">
            Question {stepNumber} of {totalSteps}
          </span>
        </div>
        
        <h2 className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight">
          {question.title}
          {question.required !== false && (
            <span className="text-red-500 ml-1">*</span>
          )}
        </h2>
        
        {question.description && (
          <p className="text-gray-600 text-lg leading-relaxed">
            {question.description}
          </p>
        )}
      </div>

      {/* Question Input */}
      <div className="mb-8">
        <QuestionRenderer
          question={question}
          answer={answer}
          onChange={(newAnswer) => onAnswerChange(question.id, newAnswer)}
        />
      </div>

      {/* Validation Message */}
      {question.required !== false && !canProceed && answer !== undefined && answer !== null && (
        <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
          <div className="flex items-center gap-2">
            <svg className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
            </svg>
            <span className="text-red-700 text-sm font-medium">
              This field is required. Please provide an answer to continue.
            </span>
          </div>
        </div>
      )}

      {/* Navigation Buttons */}
      <div className="flex justify-between items-center pt-6 border-t border-gray-200">
        <button
          onClick={onPrevious}
          disabled={isFirst}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200
            ${isFirst
              ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
              : 'bg-gray-100 text-gray-700 hover:bg-gray-200 hover:shadow-md'
            }
          `}
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Previous
        </button>

        <div className="text-center">
          <div className="text-gray-400 text-sm">
            Press Enter or click Next to continue
          </div>
        </div>

        <button
          onClick={onNext}
          disabled={!canProceed}
          className={`
            flex items-center gap-2 px-6 py-3 rounded-lg font-medium transition-all duration-200 min-w-[120px] justify-center
            ${canProceed
              ? 'bg-gradient-to-r from-amber-500 to-amber-600 text-white hover:from-amber-600 hover:to-amber-700 hover:shadow-lg transform hover:scale-105'
              : 'bg-gray-100 text-gray-400 cursor-not-allowed'
            }
          `}
        >
          {isLast ? 'Review' : 'Next'}
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>

      {/* Mobile-friendly tap hint */}
      <div className="mt-4 text-center md:hidden">
        <span className="text-gray-400 text-xs">
          Tap Next or swipe to continue
        </span>
      </div>
    </div>
  );
}